import { syntaxTree } from '@codemirror/language';
import { StateField } from '@codemirror/state';
import type { EditorState, Range } from '@codemirror/state';
import { followLink, resolve, searchFor } from '../data/links.ts';
import { AttachmentWidget, noteIdFacet, PlainImageWidget } from './attachments.ts';
import { parseConfig } from '../db/config.ts';
import { DbTable } from '../db/table.ts';
import {
  Decoration,
  EditorView,
  ViewPlugin,
  WidgetType,
  type DecorationSet,
  type ViewUpdate,
} from '@codemirror/view';

/**
 * Live-Preview im Obsidian-Sinn: Die Markup-Zeichen bleiben stehen, solange der Cursor
 * in ihrem Element steht, und blenden sich aus, sobald er es verlässt. Der Dokumenttext
 * bleibt dabei unverändert echter Markdown — das ist die Voraussetzung für
 * verlustfreien Export und für zeichenweises Mergen ab Phase 3.
 */

const HEADING = /^ATXHeading(\d)$/;

/**
 * Steht der Cursor in diesem Bereich? In einer schreibgeschützten Ansicht — der
 * geteilten Notiz — steht er nirgends: dort ist alles fertig formatiert.
 */
function touches(state: EditorState, from: number, to: number): boolean {
  if (state.readOnly) return false;
  return state.selection.ranges.some((range) => range.from <= to && range.to >= from);
}

function lineTouched(state: EditorState, pos: number): boolean {
  const line = state.doc.lineAt(pos);
  return touches(state, line.from, line.to);
}

class CheckboxWidget extends WidgetType {
  constructor(
    private readonly checked: boolean,
    private readonly from: number,
    private readonly to: number,
  ) {
    super();
  }

  override eq(other: CheckboxWidget): boolean {
    return other.checked === this.checked && other.from === this.from;
  }

  toDOM(view: EditorView): HTMLElement {
    const box = document.createElement('input');
    box.type = 'checkbox';
    box.className = 'cm-md-task';
    box.checked = this.checked;
    box.addEventListener('mousedown', (event) => {
      event.preventDefault();
      view.dispatch({
        changes: { from: this.from, to: this.to, insert: this.checked ? '[ ]' : '[x]' },
      });
    });
    return box;
  }

  override ignoreEvent(): boolean {
    return false;
  }
}

class RuleWidget extends WidgetType {
  toDOM(): HTMLElement {
    const rule = document.createElement('hr');
    rule.className = 'cm-md-rule';
    return rule;
  }
}

/** Zugangsdaten bleiben verdeckt, bis man sie ausdrücklich aufdeckt. */
class SecretWidget extends WidgetType {
  constructor(private readonly text: string) {
    super();
  }

  override eq(other: SecretWidget): boolean {
    return other.text === this.text;
  }

  toDOM(): HTMLElement {
    const wrap = document.createElement('span');
    wrap.className = 'cm-md-secret';

    const label = document.createElement('span');
    label.className = 'cm-md-secret-label';
    label.textContent = 'SECRET';

    const dots = document.createElement('span');
    dots.className = 'cm-md-secret-mask';
    dots.textContent = '•'.repeat(Math.min(Math.max(this.text.trim().length, 8), 20));

    const reveal = document.createElement('button');
    reveal.type = 'button';
    reveal.className = 'cm-md-secret-act';
    reveal.textContent = 'Zeigen';
    reveal.addEventListener('mousedown', (event) => {
      event.preventDefault();
      const shown = dots.dataset['shown'] === 'yes';
      dots.textContent = shown ? '•'.repeat(Math.min(Math.max(this.text.trim().length, 8), 20)) : this.text;
      dots.dataset['shown'] = shown ? 'no' : 'yes';
      reveal.textContent = shown ? 'Zeigen' : 'Verbergen';
    });

    const copy = document.createElement('button');
    copy.type = 'button';
    copy.className = 'cm-md-secret-act';
    copy.textContent = 'Kopieren';
    copy.addEventListener('mousedown', (event) => {
      event.preventDefault();
      void navigator.clipboard.writeText(this.text.trim()).then(() => {
        copy.textContent = 'Kopiert';
        // Die Zwischenablage nicht unbegrenzt stehen lassen.
        setTimeout(() => {
          void navigator.clipboard.writeText('').catch(() => undefined);
          copy.textContent = 'Kopieren';
        }, 30_000);
      });
    });

    wrap.append(label, dots, reveal, copy);
    return wrap;
  }

  override ignoreEvent(): boolean {
    return false;
  }
}

/**
 * Frontmatter ist kein Kasten, sondern eine einzige Monospace-Zeile über dem Titel.
 * Ein Klick setzt den Cursor hinein — und der Rohtext ist die aufgeklappte
 * Schlüssel-Wert-Liste. Zwei Zustände, eine Wahrheit.
 */
class FrontmatterWidget extends WidgetType {
  constructor(private readonly summary: string) {
    super();
  }

  override eq(other: FrontmatterWidget): boolean {
    return other.summary === this.summary;
  }

  toDOM(view: EditorView): HTMLElement {
    const line = document.createElement('div');
    line.className = 'cm-md-frontline';
    line.textContent = this.summary;
    line.title = 'Zum Aufklappen anklicken';
    line.addEventListener('mousedown', (event) => {
      event.preventDefault();
      const pos = view.posAtDOM(line);
      view.dispatch({ selection: { anchor: Math.min(pos + 4, view.state.doc.length) } });
      view.focus();
    });
    return line;
  }

  override ignoreEvent(): boolean {
    return false;
  }
}

/**
 * Ein Datenbank-Block wird als Tabelle gezeigt, solange der Cursor nicht darin steht.
 * Die Abfrage selbst bleibt gewöhnlicher Markdown-Text im Dokument — sichtbar, sobald
 * man hineinklickt oder „Abfrage bearbeiten" wählt.
 */
class DatabaseWidget extends WidgetType {
  private table: DbTable | null = null;

  constructor(private readonly source: string) {
    super();
  }

  override eq(other: DatabaseWidget): boolean {
    return other.source === this.source;
  }

  toDOM(view: EditorView): HTMLElement {
    this.table = new DbTable(parseConfig(this.source), () => {
      const pos = view.posAtDOM(this.table!.dom);
      view.dispatch({ selection: { anchor: pos }, scrollIntoView: true });
      view.focus();
    });
    return this.table.dom;
  }

  override destroy(): void {
    this.table?.destroy();
    this.table = null;
  }

  override ignoreEvent(): boolean {
    return true;
  }
}

/** Ein Codeblock ohne seine Zaunzeichen — sie sind Markup, nicht Inhalt. */
class CodeWidget extends WidgetType {
  constructor(
    private readonly code: string,
    private readonly language: string,
  ) {
    super();
  }

  override eq(other: CodeWidget): boolean {
    return other.code === this.code && other.language === this.language;
  }

  toDOM(view: EditorView): HTMLElement {
    const block = document.createElement('pre');
    block.className = 'cm-md-codeview';
    block.textContent = this.code;
    if (this.language) block.dataset['lang'] = this.language;
    block.addEventListener('mousedown', (event) => {
      event.preventDefault();
      const pos = view.posAtDOM(block);
      view.dispatch({ selection: { anchor: Math.min(pos + 4, view.state.doc.length) } });
      view.focus();
    });
    return block;
  }

  override ignoreEvent(): boolean {
    return false;
  }
}

const hidden = Decoration.replace({});

/** Tags bekommen eine der fünf Kategoriefarben, stabil über den Namen. */
function tagTone(name: string): number {
  let hash = 0;
  for (const character of name) hash = (hash * 31 + character.charCodeAt(0)) % 997;
  return (hash % 5) + 1;
}



const markClass: Record<string, string> = {
  StrongEmphasis: 'cm-md-strong',
  Emphasis: 'cm-md-em',
  Strikethrough: 'cm-md-strike',
  InlineCode: 'cm-md-code-inline',
  Link: 'cm-md-link',
  URL: 'cm-md-url',
};

/**
 * Frontmatter ist Metadaten, kein Fließtext. Es bleibt lesbar, tritt aber optisch
 * zurück, statt den Anfang jeder Notiz zu dominieren.
 */
function frontmatterLines(state: EditorState): number[] {
  if (state.doc.lines < 2 || state.doc.line(1).text.trim() !== '---') return [];
  const lines = [1];
  for (let number = 2; number <= state.doc.lines; number += 1) {
    lines.push(number);
    if (state.doc.line(number).text.trim() === '---') return lines;
  }
  return [];
}

function build(view: EditorView): DecorationSet {
  const decorations: Range<Decoration>[] = [];
  const { state } = view;
  const tree = syntaxTree(state);

  for (const number of frontmatterLines(state)) {
    decorations.push(Decoration.line({ class: 'cm-md-frontmatter' }).range(state.doc.line(number).from));
  }

  // Die Zeile, in der der Cursor steht, bekommt eine kaum sichtbare Tönung — sie ist
  // die einzige, in der das Markup sichtbar wird.
  for (const range of state.readOnly ? [] : state.selection.ranges) {
    if (!range.empty) continue;
    const line = state.doc.lineAt(range.head);
    decorations.push(Decoration.line({ class: 'cm-md-cursorline' }).range(line.from));
  }

  for (const { from, to } of view.visibleRanges) {
    tree.iterate({
      from,
      to,
      enter: (node) => {

        const heading = HEADING.exec(node.name);
        if (heading) {
          const line = state.doc.lineAt(node.from);
          decorations.push(
            Decoration.line({ class: `cm-md-h cm-md-h${heading[1]}` }).range(line.from),
          );
          return;
        }

        switch (node.name) {
          case 'HeaderMark': {
            if (lineTouched(state, node.from)) return;
            // Das Leerzeichen hinter der Raute mit ausblenden, sonst bleibt es stehen.
            const next = state.doc.sliceString(node.to, node.to + 1);
            decorations.push(hidden.range(node.from, next === ' ' ? node.to + 1 : node.to));
            return;
          }

          case 'EmphasisMark':
          case 'StrikethroughMark': {
            const parent = node.node.parent;
            if (!parent || touches(state, parent.from, parent.to)) return;
            decorations.push(hidden.range(node.from, node.to));
            return;
          }

          case 'CodeMark': {
            const parent = node.node.parent;
            if (!parent || parent.name !== 'InlineCode') return; // Fence-Zeichen bleiben stehen
            if (touches(state, parent.from, parent.to)) return;
            decorations.push(hidden.range(node.from, node.to));
            return;
          }

          case 'LinkMark':
          case 'URL': {
            const parent = node.node.parent;
            if (!parent || parent.name !== 'Link') return;
            // [[Wikilink]] liest die Grammatik zusätzlich als gewöhnlichen Link. Dessen
            // Klammern gehören zum Wikilink und werden dort behandelt, sonst
            // verschwindet beim Bearbeiten je eine Klammer.
            if (state.doc.sliceString(parent.from - 1, parent.from) === '[') return;
            if (touches(state, parent.from, parent.to)) return;
            decorations.push(hidden.range(node.from, node.to));
            return;
          }

          case 'QuoteMark': {
            if (lineTouched(state, node.from)) return;
            const next = state.doc.sliceString(node.to, node.to + 1);
            decorations.push(hidden.range(node.from, next === ' ' ? node.to + 1 : node.to));
            return;
          }

          case 'ListMark': {
            // Bei Aufgaben ersetzt die Checkbox den Aufzählungspunkt; bei normalen
            // Listen bleibt er stehen, weil er dort die Struktur trägt.
            const item = node.node.parent;
            if (!item || item.name !== 'ListItem' || !item.getChild('Task')) return;
            const next = state.doc.sliceString(node.to, node.to + 1);
            decorations.push(hidden.range(node.from, next === ' ' ? node.to + 1 : node.to));
            return;
          }

          case 'TaskMarker': {
            const checked = state.doc.sliceString(node.from, node.to).toLowerCase().includes('x');
            decorations.push(
              Decoration.replace({
                widget: new CheckboxWidget(checked, node.from, node.to),
              }).range(node.from, node.to),
            );
            return;
          }

          case 'HorizontalRule': {
            // Die --- des Frontmatters sind Begrenzer, keine Trennlinien.
            if (frontmatterLines(state).includes(state.doc.lineAt(node.from).number)) return;
            if (lineTouched(state, node.from)) return;
            decorations.push(
              Decoration.replace({ widget: new RuleWidget() }).range(node.from, node.to),
            );
            return;
          }

          case 'Blockquote': {
            for (let pos = node.from; pos <= node.to; ) {
              const line = state.doc.lineAt(pos);
              decorations.push(Decoration.line({ class: 'cm-md-quote' }).range(line.from));
              pos = line.to + 1;
            }
            return;
          }

          case 'FencedCode': {
            // Sichtbar nur, solange der Cursor darin steht — sonst zeigt das
            // State-Field ein Widget. Dann bleibt der Rohtext in Monospace stehen.
            for (let pos = node.from; pos <= node.to; ) {
              const line = state.doc.lineAt(pos);
              decorations.push(Decoration.line({ class: 'cm-md-codeblock' }).range(line.from));
              pos = line.to + 1;
            }
            return false;
          }

          default: {
            const cls = markClass[node.name];
            if (cls) decorations.push(Decoration.mark({ class: cls }).range(node.from, node.to));
          }
        }
      },
    });

    // [[Wikilinks]] und #tags kennt die Markdown-Grammatik nicht — beide per Textsuche.
    const text = state.doc.sliceString(from, to);
    for (const match of text.matchAll(/\[\[([^\]\n]+)\]\]/g)) {
      const start = from + (match.index ?? 0);
      const end = start + match[0].length;
      // Ein Link auf eine Notiz, die es noch nicht gibt, sieht anders aus — und legt
      // sie beim Anklicken an.
      const known = resolve(match[1] ?? '') !== null;
      decorations.push(
        Decoration.mark({
          class: known ? 'cm-md-wikilink' : 'cm-md-wikilink cm-md-unresolved',
        }).range(start, end),
      );
      if (!touches(state, start, end)) {
        decorations.push(hidden.range(start, start + 2));
        decorations.push(hidden.range(end - 2, end));
      }
    }
    // Anhänge: ![name](athene:id) als Bild, [name](athene:id) als Dateizeile.
    const noteId = state.facet(noteIdFacet);
    for (const match of text.matchAll(/(!?)\[([^\]\n]*)\]\(athene:([0-9a-f-]{36})\)/g)) {
      const start = from + (match.index ?? 0);
      const end = start + match[0].length;
      if (touches(state, start, end)) continue;
      decorations.push(
        Decoration.replace({
          widget: new AttachmentWidget(noteId, match[3] ?? '', match[2] ?? '', match[1] === '!'),
        }).range(start, end),
      );
    }

    // Bilder aus data:- oder http-Adressen — so kommen geteilte Notizen mit ihren
    // Bildern beim Empfänger an.
    for (const match of text.matchAll(
      /!\[([^\]\n]*)\]\((data:image\/[^)\s]+|https?:\/\/[^)\s]+)\)/g,
    )) {
      const start = from + (match.index ?? 0);
      const end = start + match[0].length;
      if (touches(state, start, end)) continue;
      decorations.push(
        Decoration.replace({
          widget: new PlainImageWidget(match[2] ?? '', match[1] ?? ''),
        }).range(start, end),
      );
    }

    for (const match of text.matchAll(/(^|\s)(#[\p{L}\p{N}_/-]{2,})/gu)) {
      const start = from + (match.index ?? 0) + (match[1]?.length ?? 0);
      const name = (match[2] ?? '').slice(1);
      decorations.push(
        Decoration.mark({ class: `cm-md-tag cm-md-tag-${tagTone(name)}` }).range(
          start,
          start + (match[2]?.length ?? 0),
        ),
      );
    }
  }

  return Decoration.set(decorations, true);
}

type BlockRange = { from: number; to: number; info: string; source: string };

/**
 * Alle Fenced-Blöcke, zeilenweise gesucht. Sie werden vollständig durch ein Widget
 * ersetzt — die Zaunzeichen selbst gehören zum Markup, nicht zum Inhalt, und stören
 * die Ruhe der Seite.
 */
function scanFencedBlocks(state: EditorState): BlockRange[] {
  const blocks: BlockRange[] = [];
  let number = 1;
  while (number <= state.doc.lines) {
    const line = state.doc.line(number);
    const opening = /^\s*```(.*)$/.exec(line.text);
    if (!opening) {
      number += 1;
      continue;
    }
    let end = number + 1;
    while (end <= state.doc.lines && !/^\s*```\s*$/.test(state.doc.line(end).text)) end += 1;
    const closing = state.doc.line(Math.min(end, state.doc.lines));
    blocks.push({
      from: line.from,
      to: closing.to,
      info: (opening[1] ?? '').trim(),
      source: state.doc.sliceString(line.to + 1, Math.max(line.to + 1, closing.from - 1)),
    });
    number = end + 1;
  }
  return blocks;
}

function frontmatterSummary(state: EditorState, lines: number[]): string {
  const entries: string[] = [];
  for (const number of lines.slice(1, -1)) {
    const text = state.doc.line(number).text;
    const colon = text.indexOf(':');
    if (colon < 1) continue;
    entries.push(`${text.slice(0, colon).trim()} ${text.slice(colon + 1).trim()}`);
  }
  if (entries.length === 0) return 'Eigenschaften';
  const shown = entries.slice(0, 2).join('  ·  ');
  const rest = entries.length - Math.min(entries.length, 2);
  return rest > 0 ? `${shown}  ·  +${rest} ${rest === 1 ? 'Feld' : 'Felder'}` : shown;
}

function buildDatabaseBlocks(state: EditorState): DecorationSet {
  const decorations: Range<Decoration>[] = [];

  const front = frontmatterLines(state);
  if (front.length >= 2) {
    const first = state.doc.line(front[0]!);
    const last = state.doc.line(front[front.length - 1]!);
    if (!touches(state, first.from, last.to)) {
      decorations.push(
        Decoration.replace({
          widget: new FrontmatterWidget(frontmatterSummary(state, front)),
          block: true,
        }).range(first.from, last.to),
      );
    }
  }

  for (const block of scanFencedBlocks(state)) {
    if (touches(state, block.from, block.to)) continue;
    const widget =
      block.info === 'athene-db'
        ? new DatabaseWidget(block.source)
        : block.info === 'secret'
          ? new SecretWidget(block.source)
          : new CodeWidget(block.source, block.info);
    decorations.push(
      Decoration.replace({ widget, block: true }).range(block.from, block.to),
    );
  }
  return Decoration.set(decorations, true);
}

export const databaseBlocks = StateField.define<DecorationSet>({
  create: (state) => buildDatabaseBlocks(state),
  update: (value, transaction) =>
    transaction.docChanged || transaction.selection
      ? buildDatabaseBlocks(transaction.state)
      : value,
  provide: (field) => EditorView.decorations.from(field),
});

export const liveMarkdown = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
      this.decorations = build(view);
    }

    update(update: ViewUpdate): void {
      if (update.docChanged || update.viewportChanged || update.selectionSet) {
        this.decorations = build(update.view);
      }
    }
  },
  { decorations: (plugin) => plugin.decorations },
);

/**
 * Klicks auf Verknüpfungen. Steht der Cursor im Link, ist er gerade in Bearbeitung —
 * dann bleibt der Klick ein gewöhnlicher Klick.
 */
export const linkClicks = EditorView.domEventHandlers({
  mousedown(event, view) {
    const target = event.target as HTMLElement | null;
    if (!target) return false;

    const wikilink = target.closest('.cm-md-wikilink');
    if (wikilink) {
      const pos = view.posAtDOM(wikilink);
      const line = view.state.doc.lineAt(pos);
      for (const match of line.text.matchAll(/\[\[([^\]\n]+)\]\]/g)) {
        const from = line.from + (match.index ?? 0);
        const to = from + match[0].length;
        if (pos < from || pos > to) continue;
        if (touches(view.state, from, to)) return false;
        event.preventDefault();
        followLink(match[1] ?? '');
        return true;
      }
      return false;
    }

    const tag = target.closest('.cm-md-tag');
    if (tag) {
      const pos = view.posAtDOM(tag);
      const line = view.state.doc.lineAt(pos);
      if (touches(view.state, line.from, line.to)) return false;
      event.preventDefault();
      searchFor(`tag:${(tag.textContent ?? '').replace(/^#/, '')}`);
      return true;
    }
    return false;
  },
});

export const liveMarkdownTheme = EditorView.theme({
  // Über eine Variable, damit Touch-Geräte auf 16px gehen können: iOS zoomt die
  // Seite, sobald ein fokussiertes Feld kleiner ist.
  '&': { fontSize: 'var(--editor-font)', height: '100%' },
  '.cm-content': {
    fontFamily: 'var(--font-body)',
    lineHeight: '1.62',
    padding: '10px 0 40vh',
    caretColor: 'var(--ink)',
  },
  '.cm-scroller': { fontFamily: 'var(--font-body)', overflowY: 'auto' },
  '.cm-line': { padding: '0 6px' },

  /* Die Cursor-Zeile ist die einzige, die Markup zeigt — und die einzige mit Tönung. */
  '.cm-md-cursorline': { background: 'var(--tint)', borderRadius: '3px' },

  '.cm-md-frontmatter': { fontFamily: 'var(--font-mono)', fontSize: '12.5px', color: 'var(--muted)' },
  '.cm-md-frontline': {
    fontFamily: 'var(--font-mono)',
    fontSize: '11px',
    letterSpacing: '0.04em',
    color: 'var(--muted)',
    borderBottom: '1px solid var(--rule)',
    padding: '2px 6px 8px',
    marginBottom: '10px',
    cursor: 'pointer',
  },

  '.cm-md-h': { fontWeight: '500', lineHeight: '1.3', letterSpacing: '-0.01em' },
  '.cm-md-h1': { fontSize: '21px', margin: '0.5em 0 0.25em' },
  '.cm-md-h2': { fontSize: '17px', margin: '1em 0 0.2em' },
  '.cm-md-h3': { fontSize: '15.5px', fontWeight: '600', margin: '0.9em 0 0.2em' },
  '.cm-md-h4, .cm-md-h5, .cm-md-h6': { fontSize: '15.5px', fontWeight: '600', margin: '0.9em 0 0.2em' },

  '.cm-md-strong': { fontWeight: '600' },
  '.cm-md-em': { fontStyle: 'italic' },
  '.cm-md-strike': { textDecoration: 'line-through', color: 'var(--muted)' },
  '.cm-md-code-inline': {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.88em',
    background: 'var(--sunk)',
    borderRadius: '3px',
    padding: '0.1em 0.3em',
  },
  '.cm-md-codeview': {
    fontFamily: 'var(--font-mono)',
    fontSize: '12.5px',
    lineHeight: '1.6',
    background: 'var(--sunk)',
    borderRadius: 'var(--radius)',
    padding: '10px 14px',
    margin: '0.5em 0',
    whiteSpace: 'pre-wrap',
    cursor: 'text',
  },
  '.cm-md-codeblock': {
    fontFamily: 'var(--font-mono)',
    fontSize: '12.5px',
    background: 'var(--sunk)',
  },
  '.cm-md-quote': { borderLeft: '2px solid var(--rule)', paddingLeft: '12px', color: 'var(--ink-soft)' },

  '.cm-md-link, .cm-md-wikilink': { color: 'var(--link)', cursor: 'pointer' },
  '.cm-md-unresolved': {
    color: 'var(--muted)',
    textDecoration: 'underline dashed',
    textUnderlineOffset: '3px',
  },
  '.cm-md-url': { color: 'var(--muted)' },

  '.cm-md-tag': { borderRadius: '3px', padding: '0.05em 0.35em', cursor: 'pointer' },
  '.cm-md-tag-1': { background: 'var(--cat-1-bg)', color: 'var(--cat-1-fg)' },
  '.cm-md-tag-2': { background: 'var(--cat-2-bg)', color: 'var(--cat-2-fg)' },
  '.cm-md-tag-3': { background: 'var(--cat-3-bg)', color: 'var(--cat-3-fg)' },
  '.cm-md-tag-4': { background: 'var(--cat-4-bg)', color: 'var(--cat-4-fg)' },
  '.cm-md-tag-5': { background: 'var(--cat-5-bg)', color: 'var(--cat-5-fg)' },

  '.cm-md-task': {
    width: 'auto',
    margin: '0 8px 0 0',
    padding: '0',
    border: 'none',
    background: 'none',
    accentColor: 'var(--ink)',
    verticalAlign: 'middle',
  },
  '.cm-md-rule': { border: '0', borderTop: '1px solid var(--rule)', margin: '0.8em 0' },

  '.cm-md-image': { display: 'inline-block', maxWidth: '100%', lineHeight: '0' },
  '.cm-md-image img': {
    display: 'block',
    maxWidth: '100%',
    height: 'auto',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--rule)',
  },
  '.cm-md-image[data-loading]': {
    minHeight: '120px',
    minWidth: '200px',
    background: 'var(--sunk)',
    borderRadius: 'var(--radius)',
  },
  '.cm-md-file': {
    display: 'inline-flex',
    gap: '10px',
    alignItems: 'baseline',
    border: '1px solid var(--rule)',
    borderRadius: 'var(--radius)',
    padding: '5px 12px',
    background: 'var(--surface)',
    fontSize: '13.5px',
  },
  '.cm-md-file-size': { fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--muted)' },
  '.cm-md-file-act': {
    font: 'inherit',
    fontSize: '12.5px',
    background: 'none',
    border: 'none',
    padding: '0',
    color: 'var(--link)',
    cursor: 'pointer',
  },
  '.cm-md-missing': { color: 'var(--warn)', borderColor: 'var(--warn)' },
  '.cm-md-secret': {
    display: 'inline-flex',
    gap: '12px',
    alignItems: 'center',
    width: '100%',
    border: '1px solid var(--rule)',
    borderRadius: 'var(--radius)',
    padding: '7px 12px',
    background: 'var(--surface)',
  },
  '.cm-md-secret-label': {
    fontFamily: 'var(--font-mono)',
    fontSize: '11px',
    letterSpacing: '0.12em',
    color: 'var(--muted)',
  },
  '.cm-md-secret-mask': { fontFamily: 'var(--font-mono)', letterSpacing: '0.12em', flex: '1' },
  '.cm-md-secret-act': {
    font: 'inherit',
    fontSize: '12.5px',
    background: 'none',
    border: 'none',
    padding: '0',
    color: 'var(--link)',
    cursor: 'pointer',
  },

  '.cm-tooltip.cm-tooltip-autocomplete': {
    border: '1px solid var(--rule)',
    borderRadius: '6px',
    background: 'var(--surface)',
    boxShadow: '0 12px 32px -20px rgba(0,0,0,.5)',
    overflow: 'hidden',
  },
  '.cm-tooltip-autocomplete ul li': {
    padding: '6px 12px',
    fontFamily: 'var(--font-body)',
    fontSize: '13.5px',
  },
  '.cm-tooltip-autocomplete ul li[aria-selected]': { background: 'var(--sunk)' },
  '.cm-completionDetail': { color: 'var(--muted)', fontStyle: 'normal', fontSize: '11px' },
});
